import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  useSpecProjectDetail,
  useUpdateSpecIntegrationTest,
} from '../../../hooks/useSpecRequirements.js';
import { FormatHint } from './FormatHint.js';
import { IntegrationTestEditor } from './IntegrationTestEditor.js';
import { SpecTable } from './SpecTable.js';

/**
 * 列の見出しの翻訳キー。読む表と編集フォームで同じ並び。
 * module scope では hook を呼べないので鍵で持ち、描画時に `t()` で解く。
 * @graph-connects none
 */
const HEADING_KEYS = [
  'spec.integrationTest.columns.scene',
  'spec.integrationTest.columns.scenario',
  'spec.integrationTest.columns.screen',
  'spec.integrationTest.columns.given',
  'spec.integrationTest.columns.operation',
  'spec.integrationTest.columns.expected',
  'spec.integrationTest.columns.covers',
] as const;

export interface IntegrationTestPageProps {
  /** 開く案件 */
  pjKey: string;
}

/**
 * 総合テストだけを幅いっぱいで読み書きするページ。
 *
 * **要件定義の 1 画面に収まらないので別ページにする。** 列が 7 つあり、事前条件は期待する結果が
 * 1 つに決まるところまで書くので長い。節の中の幅では、読むのも書くのも成り立たなかった。
 * 節の側はシナリオ単位に畳んだ一覧だけを出し、ここへ送る。
 *
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件の総合テストを幅いっぱいの表で読み、シナリオを編集して保存する
 * @graph-connects /spec-requirements [calls] 成果物の取得
 * @graph-connects IntegrationTestEditor [calls] 総合テストの編集
 */
export function IntegrationTestPage({ pjKey }: IntegrationTestPageProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const detailQuery = useSpecProjectDetail(pjKey);
  const save = useUpdateSpecIntegrationTest(pjKey);

  const detail = detailQuery.data ?? null;
  const integrationTest = detail?.integrationTest ?? null;
  const actionKey = integrationTest === null ? 'spec.action.write' : 'spec.action.edit';

  return (
    <div className="p-6">
      <Link
        to="/projects/spec/$pjKey"
        params={{ pjKey }}
        className="text-sm text-[var(--color-accent-primary)] hover:underline"
      >
        {t('spec.integrationTest.back')}
      </Link>

      <div className="mt-2 flex items-baseline justify-between border-b-2 border-[var(--color-border-hover)] pb-3">
        <div>
          <h1 className="text-xl font-bold">{t('spec.artifact.integrationTest')}</h1>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">
            {detail === null ? pjKey : `${pjKey}　${detail.pjTitle}`}
          </p>
        </div>
        {editing ? null : (
          <button
            type="button"
            className="text-xs px-3 py-1 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)] cursor-pointer disabled:cursor-not-allowed"
            aria-label={t('spec.section.editAria', {
              heading: t('spec.artifact.integrationTest'),
              action: t(actionKey),
            })}
            onClick={() => {
              setWarnings([]);
              setEditing(true);
            }}
          >
            {t(actionKey)}
          </button>
        )}
      </div>

      {warnings.length > 0 && !editing ? (
        <div className="mt-3 border-l-4 border-[var(--color-warning)] bg-[var(--color-warning-muted)] px-4 py-3 rounded-r">
          <h2 className="text-xs font-semibold text-[var(--color-warning-text)]">
            {t('spec.savedWithWarnings')}
          </h2>
          <ul className="mt-1 text-sm list-disc pl-5 text-[var(--color-text-primary)]">
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {detailQuery.error !== null ? (
        <p className="mt-6 text-sm text-[var(--color-text-secondary)]">
          {t('spec.integrationTest.readFailed')}
        </p>
      ) : detail === null ? (
        <p className="mt-6 text-sm text-[var(--color-text-muted)]">{t('spec.loading')}</p>
      ) : editing ? (
        <>
          <FormatHint artifact="integration_test" label={t('spec.artifact.integrationTest')} />
          <IntegrationTestEditor
            integrationTest={integrationTest}
            saving={save.isPending}
            error={save.error}
            warnings={warnings}
            onSave={(rows) => {
              save.mutate(rows, {
                onSuccess: (result) => {
                  setWarnings(result.warnings);
                  setEditing(false);
                },
              });
            }}
            onCancel={() => {
              save.reset();
              setEditing(false);
            }}
          />
        </>
      ) : integrationTest === null ? (
        <p className="mt-6 text-sm text-[var(--color-text-muted)]">
          {t('spec.integrationTest.empty')}
        </p>
      ) : (
        <SpecTable
          numbered={{ prefix: 'T', heading: t('spec.section.rowNumber') }}
          headings={HEADING_KEYS.map((key) => t(key))}
          rows={integrationTest.rows.map((row, index) => ({
            key: `${String(index)}/${row.scenario}`,
            no: row.no,
            cells: [
              row.scene,
              row.scenario,
              row.screen,
              row.given,
              row.operation,
              row.expected,
              row.covers,
            ],
          }))}
        />
      )}
    </div>
  );
}
